import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { organizationsAPI } from '../../api';

// This callback handles Auth0 redirect processing and token storage
const AuthCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing authentication...');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    const processCallback = async () => {
      try {
        setStatus('Processing authentication...');
        console.log('Auth0 callback component loaded, current URL:', window.location.href);

        // Log query parameters for debugging
        const urlParams = new URLSearchParams(window.location.search);
        console.log('Query parameters:', {
          code: urlParams.get('code'),
          state: urlParams.get('state')
        });

        // Since onRedirectCallback in index.js should handle the Auth0 callback,
        // this component just handles the UI and redirects
        setStatus('Authentication successful, redirecting...');

        // Wait a moment for Auth0 to process
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Check for invitation flow - look for token in multiple places
        let invitationToken = searchParams.get('invitation_token');
        
        // Check sessionStorage if not in URL
        if (!invitationToken) {
          invitationToken = sessionStorage.getItem('invitation_token');
          console.log('Found invitation token in sessionStorage:', invitationToken);
        }
        
        // Check localStorage as backup
        if (!invitationToken) {
          invitationToken = localStorage.getItem('invitation_token');
          console.log('Found invitation token in localStorage:', invitationToken);
        }
        
        // Check if there's invitation data with timestamp
        if (!invitationToken) {
          try {
            const invitationData = JSON.parse(sessionStorage.getItem('invitation_data') || '{}');
            if (invitationData.token) {
              invitationToken = invitationData.token;
              console.log('Found invitation token in invitation_data:', invitationToken);
            }
          } catch (e) {
            console.error('Error parsing invitation_data:', e);
          }
        }
        
        if (invitationToken) {
          setStatus('Processing invitation...');
          console.log('Processing invitation with token:', invitationToken);

          // Store invitation data for the AcceptInviteComplete component
          sessionStorage.setItem('invitation_data', JSON.stringify({
            token: invitationToken,
            tenantId: searchParams.get('tenant_id'),
            timestamp: Date.now()
          }));

          // Navigate to invitation completion page
          navigate('/auth/accept-invite-complete', { replace: true });
          return;
        }

        // For normal authentication, try to get organization info for proper redirect
        let redirectUrl = '/dashboard'; // Fallback
        
        try {
          setStatus('Fetching your organization...');
          console.log('🔄 AuthCallback: Getting access token...');
          
          // Try to get organization unique ID from backend
          const token = await getAccessTokenSilently();
          console.log('✅ AuthCallback: Got access token successfully');
          
          // Make a quick API call to get organization info
          console.log('🔄 AuthCallback: Fetching organization data...');
          const organizations = await organizationsAPI.getMyOrganization(token);
          console.log('📊 AuthCallback: Organizations response:', organizations);
          console.log('📊 AuthCallback: Organizations type:', typeof organizations);
          console.log('📊 AuthCallback: Organizations length:', Array.isArray(organizations) ? organizations.length : 'Not an array');
          
          if (Array.isArray(organizations) && organizations.length > 0) {
            const firstOrg = organizations[0];
            console.log('📊 AuthCallback: First organization:', firstOrg);
            console.log('📊 AuthCallback: First organization unique_id:', firstOrg.unique_id);
            
            if (firstOrg.unique_id) {
              redirectUrl = `/org/${firstOrg.unique_id}/dashboard`;
              console.log('✅ AuthCallback: Redirecting to organization-specific dashboard:', redirectUrl);
            } else {
              console.warn('⚠️ AuthCallback: Organization found but no unique_id property');
            }
          } else if (organizations && organizations.unique_id) {
            // Handle case where API returns single organization object instead of array
            redirectUrl = `/org/${organizations.unique_id}/dashboard`;
            console.log('✅ AuthCallback: Redirecting to organization-specific dashboard (single object):', redirectUrl);
          } else {
            console.warn('⚠️ AuthCallback: No organizations found or invalid response structure');
            // For users without organizations, redirect to root to trigger onboarding flow
            redirectUrl = '/';
          }
        } catch (orgError) {
          console.error('❌ AuthCallback: Could not fetch organization for redirect:', orgError);
          console.error('❌ AuthCallback: Error details:', {
            message: orgError.message,
            stack: orgError.stack,
            name: orgError.name
          });
          
          // Check if this is a new user scenario
          if (orgError.message?.includes('No organization information found in token')) {
            console.warn('🔧 AuthCallback: JWT missing organization claims - likely new user');
            setStatus('Setting up your account...');
            redirectUrl = '/'; // Let AppRoutes handle onboarding flow
          } else if (orgError.message?.includes('User not found') || 
                     orgError.message?.includes('Failed to fetch organization') ||
                     orgError.message?.includes('User not associated with any organization')) {
            console.warn('👤 AuthCallback: New user detected - redirecting to onboarding flow');
            setStatus('Setting up your organization...');
            redirectUrl = '/'; // Let AppRoutes handle onboarding flow
          } else {
            console.warn('⚠️ AuthCallback: Unknown error - falling back to root for proper routing');
            redirectUrl = '/'; // Let AppRoutes handle proper routing
          }
        }

        console.log('🎯 AuthCallback: Final redirect URL:', redirectUrl);

        const returnTo = sessionStorage.getItem('auth0_returnTo');
        console.log('📦 AuthCallback: auth0_returnTo from session storage:', returnTo);
        
        // If we successfully got an organization-specific URL, use it regardless of returnTo
        const finalUrl = redirectUrl !== '/dashboard' ? redirectUrl : (returnTo || redirectUrl);
        console.log('🎯 AuthCallback: Final navigation URL:', finalUrl);
        
        sessionStorage.removeItem('auth0_returnTo'); // Clean up
        navigate(finalUrl, { replace: true });

      } catch (err) {
        console.error('Auth0 callback error:', err);
        console.log('State parameter during error:', searchParams.get('state'));
        setError(err.message || 'Authentication failed');
        setStatus('Authentication failed');

        // Clear any potentially corrupted auth state
        localStorage.removeItem('invitation_data');

        // Redirect to signin after error display
        setTimeout(() => {
          navigate('/signin', { replace: true });
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    // Simply call processCallback - let Auth0 SDK handle all parameter validation
    processCallback();
  }, [navigate, searchParams, getAccessTokenSilently]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Completing Sign In
          </h2>
          <p className="mt-2 text-gray-600">
            {status}
          </p>
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-100 rounded-full h-12 w-12 flex items-center justify-center mx-auto">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Authentication Failed
          </h2>
          <p className="mt-2 text-gray-600">
            {error}
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Redirecting to sign in page in a few seconds...
          </p>
          <button 
            onClick={() => navigate('/signin', { replace: true })}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Sign In
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;